import type { StorefrontProduct } from '../../api/client';
import { isColourOption, swatchColours } from './colorSwatch';
import { useElementBox, useElementRender, type ElementComponent } from './elementRenderContext';
import { numberProp } from './elementProps';
import { useProduct } from './productContext';
import './elementSwatches.css';
import { named, useShopNames } from '../../commerce/shopNames';

// A product's colours as a row of round swatches under its card, as Shopify's Modular prints them. They describe the
// product rather than act on it: the card is already the link, and a row of 22px targets would be too small to tap.

/** One swatch: the value as the merchant wrote it, and the colour it stands for. */
export type Swatch = { value: string; colour: string };

/** What the builder shows on a card with no product yet, so the row can be placed and styled. */
const SAMPLE: readonly Swatch[] = ['#c0603c', '#efe6d2', '#f2d4dd', '#6e8f66'].map((colour) => ({ value: colour, colour }));

// Reads a product's colour option as swatches, or none when it has no colour option or a value with no known colour.
export function productSwatches(product: Pick<StorefrontProduct, 'options'> | null): Swatch[] {
  const option = product?.options?.find((entry) => isColourOption(entry.name));
  const colours = option ? swatchColours(option.values) : null;
  return option && colours ? option.values.map((value) => ({ value, colour: colours.get(value) ?? '' })) : [];
}

// Draws the card's product's colours, at most as many as the merchant allows, or nothing for a product without colours.
export const SwatchesElement: ElementComponent = ({ node }) => {
  const box = useElementBox(node);
  const { editing } = useElementRender();
  const product = useProduct();
  const said = useShopNames();
  const found = productSwatches(product);
  const swatches = (found.length === 0 && editing && !product ? SAMPLE : found).slice(0, Math.max(1, numberProp(node.props, 'limit', 7)));
  if (swatches.length === 0) return null;
  const names = swatches.map((swatch) => swatch.value).join(', ');
  return (
    <ul {...box} className={[box.className, 'xe-swatches'].filter(Boolean).join(' ')} aria-label={named(said.colours, { names })}>
      {swatches.map((swatch) => <li key={swatch.value} className="xe-swatch" title={swatch.value} style={{ backgroundColor: swatch.colour }} />)}
    </ul>
  );
};
